// Task urgency classification + sorting.
// Buckets:
//   late      — open task whose deadline is today or in the past (RED)
//   upcoming  — open task whose deadline is within the next 7 days (YELLOW)
//   completed — status = completed (GREEN)
//   normal    — everything else: no deadline, deadline > 7 days, or cancelled (default colors, sorted last)

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export function taskUrgency(task) {
  const status = task?.status;
  const deadlineStr = task?.deadline;
  const now = new Date();
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  // Completed always wins — it's a terminal state worth celebrating
  if (status === "completed") {
    return {
      bucket: "completed",
      sortKey: 3,
      label: "Completed",
      ringClass: "ring-1 ring-emerald-300/60",
      bgClass: "bg-emerald-50/40",
      badgeClass: "bg-emerald-100 text-emerald-800 border-emerald-200",
      dotClass: "bg-emerald-500",
    };
  }

  if (status === "cancelled") {
    return {
      bucket: "normal",
      sortKey: 4,
      label: "Cancelled",
      ringClass: "",
      bgClass: "",
      badgeClass: "bg-stone-100 text-stone-600 border-stone-200",
      dotClass: "bg-stone-400",
    };
  }

  if (deadlineStr) {
    const deadline = new Date(deadlineStr);
    if (!Number.isNaN(deadline.getTime())) {
      const diffMs = deadline.getTime() - endOfToday.getTime();
      const daysFromEndOfToday = Math.floor(diffMs / ONE_DAY_MS);
      if (deadline <= endOfToday) {
        return {
          bucket: "late",
          sortKey: 0,
          label: deadline < now ? "Late" : "Due today",
          ringClass: "ring-1 ring-red-300/70",
          bgClass: "bg-red-50/50",
          badgeClass: "bg-red-100 text-red-800 border-red-200",
          dotClass: "bg-red-500",
        };
      }
      if (daysFromEndOfToday <= 6) {
        return {
          bucket: "upcoming",
          sortKey: 1,
          label: daysFromEndOfToday <= 0 ? "Tomorrow" : `In ${daysFromEndOfToday + 1}d`,
          ringClass: "ring-1 ring-amber-300/60",
          bgClass: "bg-amber-50/50",
          badgeClass: "bg-amber-100 text-amber-800 border-amber-200",
          dotClass: "bg-amber-500",
        };
      }
    }
  }

  return {
    bucket: "normal",
    sortKey: 2,
    label: null,
    ringClass: "",
    bgClass: "",
    badgeClass: "",
    dotClass: "",
  };
}

// Sort: late → upcoming → normal → completed → cancelled.
// Within a bucket: deadline ascending (tasks without a deadline last).
export function sortByUrgency(tasks) {
  return [...(tasks || [])].sort((a, b) => {
    const ua = taskUrgency(a);
    const ub = taskUrgency(b);
    if (ua.sortKey !== ub.sortKey) return ua.sortKey - ub.sortKey;
    const ad = a?.deadline ? new Date(a.deadline).getTime() : Number.POSITIVE_INFINITY;
    const bd = b?.deadline ? new Date(b.deadline).getTime() : Number.POSITIVE_INFINITY;
    if (ad !== bd) return ad - bd;
    // Fall back to created_at desc so newest float up among equals
    const ac = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const bc = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return bc - ac;
  });
}
